import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Save, Loader2, Plus, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Admin: withdrawal gate rules (invites required per withdrawal number). */
export default function AdminWithdrawGateCard() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newN, setNewN] = useState("");
  const [newV, setNewV] = useState("");
  const load = () => axios.get(`${API}/admin/withdraw-gate`).then((x) => setR(x.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const x = await axios.put(`${API}/admin/withdraw-gate`, patch);
      setR(x.data);
      toast.success("Regras de saque atualizadas");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    setBusy(false);
  };
  if (!r) return null;
  const overrides = r.overrides || {};
  return (
    <Card className="p-4 space-y-4" data-testid="admin-withdraw-gate">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-primary" />
          <div>
            <h3 className="font-bold">Trava de saque por convites</h3>
            <p className="text-xs text-muted-foreground">A partir do 2º saque, o usuário precisa ter convidado um mínimo de amigos (cadastrados pelo link dele).</p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!r.enabled} disabled={busy} onCheckedChange={(v) => save({ enabled: v })} data-testid="withdraw-gate-enabled" /> {r.enabled ? "Ativa" : "Desativada"}</label>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 items-end">
        <div>
          <Label className="text-xs">Convidados exigidos no 2º saque</Label>
          <Input type="number" min={0} value={r.base_required} onChange={(e) => setR({ ...r, base_required: e.target.value })} data-testid="withdraw-gate-base" />
        </div>
        <div>
          <Label className="text-xs">Acréscimo a cada saque seguinte</Label>
          <Input type="number" min={0} value={r.step} onChange={(e) => setR({ ...r, step: e.target.value })} data-testid="withdraw-gate-step" />
        </div>
        <Button onClick={() => save({ base_required: parseInt(r.base_required) || 0, step: parseInt(r.step) || 0 })} disabled={busy} className="rounded-full" data-testid="withdraw-gate-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Salvar regra
        </Button>
      </div>
      <div>
        <Label className="text-xs">Exceções por número do saque (sobrescrevem a regra)</Label>
        <div className="flex flex-wrap gap-2 mt-1" data-testid="withdraw-gate-overrides">
          {Object.entries(overrides).sort((a, b) => +a[0] - +b[0]).map(([n, v]) => (
            <span key={n} className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs" data-testid={`withdraw-gate-override-${n}`}>
              {n}º saque = {v} convidados
              <button onClick={() => { const o = { ...overrides }; delete o[n]; save({ overrides: o }); }} className="text-muted-foreground hover:text-destructive" aria-label="Remover" data-testid={`withdraw-gate-override-remove-${n}`}><Trash2 className="w-3 h-3" /></button>
            </span>
          ))}
          {Object.keys(overrides).length === 0 && <span className="text-xs text-muted-foreground">Nenhuma exceção.</span>}
        </div>
        <div className="flex gap-2 mt-2 items-center">
          <Input type="number" min={2} placeholder="Nº do saque" value={newN} onChange={(e) => setNewN(e.target.value)} className="w-32" data-testid="withdraw-gate-new-n" />
          <Input type="number" min={0} placeholder="Convidados" value={newV} onChange={(e) => setNewV(e.target.value)} className="w-32" data-testid="withdraw-gate-new-v" />
          <Button variant="outline" size="sm" className="rounded-full" disabled={!newN || newV === ""} onClick={() => { save({ overrides: { ...overrides, [newN]: parseInt(newV) } }); setNewN(""); setNewV(""); }} data-testid="withdraw-gate-add-override"><Plus className="w-3.5 h-3.5 mr-1" /> Adicionar</Button>
        </div>
      </div>
      <div>
        <Label className="text-xs">Pré-visualização</Label>
        <div className="flex flex-wrap gap-1.5 mt-1" data-testid="withdraw-gate-preview">
          {r.preview.map((p) => <span key={p.n} className={`text-[11px] px-2 py-0.5 rounded-full border ${p.required === 0 ? "border-emerald-400 text-emerald-700" : "border-border"}`}>{p.n}º: {p.required === 0 ? "livre" : `${p.required}`}</span>)}
        </div>
      </div>
    </Card>
  );
}
